/**
 * DialysisCommandCenterPage — /dialysis
 *
 * A dedicated "dialysis command center" landing page that pairs the new
 * Hemodialysis Product & Care marketplace category with the AAMI ST108 and
 * Hemodialysis Water Treatment compliance resources.
 *
 * Purpose:
 *  - Compete head-to-head with Fresenius / DaVita / Rockwell in the Gulf region
 *    with a single link buyers, RFP responders, and biz-dev decks can share.
 *  - Rank organically for "hemodialysis water treatment Houston", "dialysis
 *    concentrates Texas", "AAMI 13959 compliance", etc.
 *  - Route buyers into either (a) marketplace SKUs for reorder or (b) a
 *    compliance audit for new-build / retrofit work.
 */
import SEO, { technicalArticleSchema, waterServiceSchema } from "@/components/SEO";
import SiteHeader from "@/components/SiteHeader";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Droplets,
  FlaskConical,
  Beaker,
  Activity,
  ShieldCheck,
  Truck,
  Building2,
  CheckCircle2,
  ArrowRight,
  Phone,
  Mail,
  MapPin,
  Award,
  FileText,
  Gauge,
} from "lucide-react";

// ────────────────────────────────────────────────────────────────────────────
// Data
// ────────────────────────────────────────────────────────────────────────────

const PILLARS = [
  {
    icon: Droplets,
    title: "Water Treatment",
    kicker: "AAMI 13959 / 11663",
    text: "Central RO trains (two-pass + DI polish + ultrafilters) engineered for chair-count, disinfection cycle, and CMS ESRD V-tag audit trail.",
    to: "/solutions/water-treatment",
  },
  {
    icon: FlaskConical,
    title: "Concentrates & Additives",
    kicker: "Diasol · Dryasol · Citrisol",
    text: "Full acid concentrate lines with 35X / 36.83X / 45X proportioning, dextrose 100/200, K 0-4, Ca 0-3.5. Pump-off delivery, empty-drum haul-away.",
    to: "/marketplace?category=hemodialysis",
  },
  {
    icon: Beaker,
    title: "Additive Spikes",
    kicker: "K+ · Ca2+ · Ascorbic",
    text: "Color-coded, see-through packaging spike additives sized for 3.43 L or 3.785 L jugs. 3-year shelf life on dry powder.",
    to: "/marketplace?category=hemodialysis",
  },
  {
    icon: Gauge,
    title: "Equipment",
    kicker: "Mixers · Scales",
    text: "40 / 80-gallon automated concentrate mixers + Solace 1,000 lb in-floor stretcher-compatible dialysis scales with touchscreen indicator.",
    to: "/marketplace?category=hemodialysis",
  },
  {
    icon: Activity,
    title: "Consumables",
    kicker: "Infusion sets",
    text: "UV-protected infusion sets for photosensitive drugs + FBV safety infusion sets that auto-block flow and prevent air-embolism.",
    to: "/marketplace?category=hemodialysis",
  },
  {
    icon: ShieldCheck,
    title: "Compliance",
    kicker: "CMS ESRD · Joint Commission",
    text: "Quarterly Legionella + HPC testing, ST108 water audits, WMP builds, and audit-ready binders that map to every V-tag surveyors ask for.",
    to: "/resources/hemodialysis-water-treatment",
  },
];

const STANDARDS_STACK = [
  { code: "AAMI 13959", note: "Water for hemodialysis and related therapies (ISO-aligned)" },
  { code: "AAMI 11663", note: "Quality of dialysis fluid — dialysate limits" },
  { code: "AAMI 23500", note: "Ultra-pure dialysate framework" },
  { code: "AAMI ST108", note: "Water for processing medical devices (Category 1 → 3)" },
  { code: "CMS 42 CFR §494.40", note: "ESRD Conditions for Coverage (V178–V190)" },
  { code: "ASHRAE 188", note: "Building water Legionella risk management" },
  { code: "Joint Commission EC.02.05.01", note: "Utility systems / water safety" },
];

const COVERAGE = [
  { state: "Texas", locales: "Houston · TMC · Dallas · San Antonio · Austin · McAllen" },
  { state: "Louisiana", locales: "New Orleans · Baton Rouge · Shreveport · Lafayette" },
  { state: "Alabama", locales: "Birmingham · Mobile · Montgomery · Huntsville" },
  { state: "Mississippi", locales: "Jackson · Gulfport · Hattiesburg · Tupelo" },
];

const COMPARE = [
  {
    them: "Fresenius Medical Care",
    us: "Locally dispatched from Houston. No 6-week concentrate lead times. Same-day rush delivery when a proportioning failure hits.",
  },
  {
    them: "DaVita / Rockwell distribution",
    us: "Non-GPO — no minimum monthly commitment, no exclusivity clauses. Mix concentrate suppliers and equipment vendors freely.",
  },
  {
    them: "Regional dialysis distributors",
    us: "Full-stack: water + concentrate + additives + equipment + consumables + compliance audits — one purchase order, one phone number.",
  },
];

const FAQS = [
  {
    q: "How is Sterile Mate different from Fresenius / DaVita / Rockwell?",
    a: "We are a non-GPO, locally dispatched partner headquartered in Houston. Unlike national suppliers, we do not require exclusivity or minimum monthly commitments. You can source acid concentrate from us and equipment from another vendor with no penalty. Our drivers pump-off directly into your bulk tanks and haul away empty drums each delivery — same visit.",
  },
  {
    q: "Do you supply both in-center and acute hospital dialysis units?",
    a: "Yes. We serve free-standing dialysis clinics, hospital acute dialysis units, and home dialysis programs across Texas, Louisiana, Alabama, and Mississippi. Every SKU we sell is compatible with Fresenius 2008/5008, NxStage, and B. Braun Dialog+ machines unless we flag otherwise.",
  },
  {
    q: "What water quality standards do your systems meet?",
    a: "Every water treatment train we install is engineered to meet ANSI/AAMI/ISO 13959 (water for hemodialysis) and ANSI/AAMI/ISO 11663 (dialysate quality). Optional upgrade paths meet AAMI 23500 ultra-pure dialysate for facilities running online hemodiafiltration.",
  },
  {
    q: "How do you help with CMS ESRD Condition-of-Coverage surveys?",
    a: "We deliver an audit-ready binder mapped to V-tags V178–V190 (water + dialysate quality, equipment, documentation). Monthly microbial + endotoxin cultures, quarterly trace-metal panels, and annual comprehensive AAMI 13959 Table A.1 testing are all documented under one contract.",
  },
  {
    q: "How quickly can you respond to an emergency (RO failure, contamination)?",
    a: "Gulf-region dispatch is one business day for scheduled work and same-day for emergencies out of our Houston operations center. We keep replacement RO membranes, DI tanks, carbon media, and endotoxin ultrafilters staged for rapid deployment.",
  },
];

// ────────────────────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────────────────────

export default function DialysisCommandCenterPage() {
  const canonical = "/dialysis";
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      technicalArticleSchema({
        headline: "Hemodialysis Command Center — Water, Concentrates, Equipment & Compliance",
        description:
          "Sterile Mate's Gulf-region dialysis command center: AAMI 13959/11663 water treatment, Diasol/Citrisol acid concentrates, additive spikes, concentrate mixers, in-floor dialysis scales, infusion sets, and CMS ESRD survey-ready compliance binders. Serving TX, LA, AL, and MS.",
        url: canonical,
        datePublished: "2026-02-24",
        dateModified: "2026-02-24",
        keywords: [
          "hemodialysis command center",
          "dialysis water treatment Houston",
          "AAMI 13959 compliance Texas",
          "dialysis acid concentrate distributor",
          "CMS ESRD water compliance",
          "Fresenius alternative distributor",
          "DaVita alternative distributor",
          "Rockwell Medical concentrates alternative",
          "hemodialysis Gulf region",
        ],
      }),
      waterServiceSchema,
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white" data-testid="dialysis-command-center-page">
      <SEO
        canonical={canonical}
        title="Hemodialysis Command Center — Water, Concentrates, Equipment & Compliance (TX·LA·AL·MS)"
        description="Full-stack Gulf-region hemodialysis partner: AAMI 13959 water treatment, Diasol/Citrisol concentrates, additives, mixers, scales, infusion sets, and CMS ESRD survey-ready compliance."
        keywords={[
          "hemodialysis command center",
          "dialysis water treatment Houston",
          "dialysis acid concentrate distributor",
          "AAMI 13959 compliance",
          "Fresenius alternative",
          "DaVita alternative",
          "Rockwell Medical concentrates alternative",
        ]}
        schema={schema}
      />
      <SiteHeader />

      {/* ── HERO ────────────────────────────────────────────────────────── */}
      <section className="relative bg-gradient-to-br from-[#0a1a35] via-[#0b2447] to-[#0056A8] text-white overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(45,212,191,0.20),transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(34,211,238,0.15),transparent_60%)]" />
        <div className="relative max-w-7xl mx-auto px-6 py-24">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <p className="text-[10px] font-bold tracking-[0.32em] uppercase text-teal-300 mb-4">
                Hemodialysis Command Center · Gulf Region
              </p>
              <h1 className="text-4xl md:text-6xl font-black leading-[1.03] max-w-3xl">
                One partner for every drop, every drum, every dialysis chair.
              </h1>
              <p className="mt-6 text-lg md:text-xl text-white/85 max-w-2xl leading-relaxed">
                Water treatment, acid concentrates, additive spikes, mixers, scales, infusion sets,
                and CMS ESRD compliance — dispatched from Houston to hemodialysis facilities across{" "}
                <strong className="text-white">Texas, Louisiana, Alabama, and Mississippi.</strong>
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button
                  asChild
                  size="lg"
                  className="bg-teal-400 hover:bg-teal-300 text-slate-900 font-bold shadow-lg shadow-teal-500/25"
                  data-testid="dialysis-cta-marketplace"
                >
                  <Link to="/marketplace?category=hemodialysis">
                    <FlaskConical className="w-5 h-5 mr-2" />
                    Browse Dialysis Marketplace
                  </Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10"
                  data-testid="dialysis-cta-audit"
                >
                  <a href="mailto:info@sterilemate.com?subject=Dialysis facility compliance audit">
                    <ShieldCheck className="w-5 h-5 mr-2" />
                    Request Compliance Audit
                  </a>
                </Button>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/70">
                <span className="inline-flex items-center gap-2"><Award className="w-4 h-4 text-teal-300" /> certified small business Certified</span>
                <span className="inline-flex items-center gap-2"><Truck className="w-4 h-4 text-teal-300" /> Same-day Gulf-region dispatch</span>
                <span className="inline-flex items-center gap-2"><Building2 className="w-4 h-4 text-teal-300" /> Houston HQ · TMC-adjacent</span>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-teal-900/40 ring-1 ring-white/10">
                <img
                  src="/dialysis-hero.jpg"
                  alt="A dialysis nurse checking a hemodialysis machine while a patient is treated in a modern dialysis center"
                  className="w-full h-auto object-cover"
                  loading="eager"
                  data-testid="dialysis-hero-image"
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-900/80 to-transparent p-5">
                  <p className="text-xs uppercase tracking-widest text-teal-300 font-bold mb-1">
                    Real care, real chairs
                  </p>
                  <p className="text-white text-sm md:text-base font-semibold">
                    Every SKU we sell is one that shows up next to a patient — so we treat every order like it matters.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── STAT STRIP ─────────────────────────────────────────────────── */}
      <section className="bg-slate-900 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { n: "500+", l: "Dialysis SKUs" },
            { n: "4", l: "States served" },
            { n: "24 hr", l: "Emergency dispatch" },
            { n: "V178–V190", l: "V-tags mapped" },
          ].map((s) => (
            <div key={s.l} className="text-center">
              <p className="text-3xl md:text-4xl font-black text-white leading-none">{s.n}</p>
              <p className="mt-2 text-xs uppercase tracking-wider text-white/50">{s.l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── FULL-STACK PILLARS ─────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="max-w-3xl mb-10">
          <p className="text-[10px] font-bold tracking-[0.32em] uppercase text-teal-700 mb-3">The Full Stack</p>
          <h2 className="text-3xl md:text-4xl font-black text-slate-900">Six pillars. One phone number.</h2>
          <p className="mt-4 text-slate-600 text-lg">
            Every hemodialysis facility has to source water, chemistry, equipment, consumables, and
            compliance from someone. Most spend months coordinating five different vendors. We
            consolidate the stack.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {PILLARS.map((p) => (
            <Link
              key={p.title}
              to={p.to}
              className="group rounded-2xl border border-slate-200 bg-white p-6 hover:shadow-xl hover:-translate-y-0.5 transition-all"
              data-testid={`dialysis-pillar-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center mb-4 shadow-md shadow-teal-500/20">
                <p.icon className="w-5 h-5 text-white" />
              </div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-teal-700 mb-1">{p.kicker}</p>
              <p className="text-xl font-black text-slate-900 mb-2">{p.title}</p>
              <p className="text-sm text-slate-600 leading-relaxed">{p.text}</p>
              <p className="mt-4 text-sm font-semibold text-teal-700 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                Explore <ArrowRight className="w-4 h-4" />
              </p>
            </Link>
          ))}
        </div>
      </section>

      {/* ── STANDARDS STACK ─────────────────────────────────────────────── */}
      <section className="bg-slate-50 border-y border-slate-200 py-16">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <p className="text-[10px] font-bold tracking-[0.32em] uppercase text-teal-700 mb-3">Regulatory Stack</p>
            <h2 className="text-3xl md:text-4xl font-black text-slate-900">
              Every standard your surveyor references.
            </h2>
            <p className="mt-4 text-slate-600">
              Our compliance binder maps every SOP, log entry, and test result to the specific
              clause a CMS or Joint Commission surveyor will cite. Zero mystery on audit day.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button asChild variant="outline" data-testid="dialysis-link-st108">
                <Link to="/resources/aami-st108">Read the ST108 guide</Link>
              </Button>
              <Button asChild variant="outline" data-testid="dialysis-link-hemodialysis-water">
                <Link to="/resources/hemodialysis-water-treatment">Hemodialysis water guide</Link>
              </Button>
              <Button asChild variant="outline" data-testid="dialysis-link-wmp">
                <Link to="/resources/joint-commission-water-management">Joint Commission WMP</Link>
              </Button>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 text-slate-800">
                  <tr>
                    <th className="text-left px-5 py-3 font-bold w-56">Standard / Code</th>
                    <th className="text-left px-5 py-3 font-bold">What it covers</th>
                  </tr>
                </thead>
                <tbody>
                  {STANDARDS_STACK.map((s) => (
                    <tr key={s.code} className="border-t border-slate-100">
                      <td className="px-5 py-3 font-mono font-semibold text-slate-900">{s.code}</td>
                      <td className="px-5 py-3 text-slate-700">{s.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* ── HEAD TO HEAD ────────────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="max-w-3xl mb-10">
          <p className="text-[10px] font-bold tracking-[0.32em] uppercase text-amber-700 mb-3">Head to Head</p>
          <h2 className="text-3xl md:text-4xl font-black text-slate-900">
            Why Gulf-region hospitals switch to us.
          </h2>
          <p className="mt-4 text-slate-600 text-lg">
            National dialysis distributors optimize for scale. We optimize for the phone call at 6 AM
            when your RO membrane just failed.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {COMPARE.map((c) => (
            <div key={c.them} className="rounded-2xl border border-slate-200 bg-white p-6">
              <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">Compared to</p>
              <p className="text-lg font-black text-slate-900 mb-4">{c.them}</p>
              <div className="flex items-start gap-2 text-sm text-slate-700 leading-relaxed">
                <CheckCircle2 className="w-5 h-5 text-teal-600 shrink-0 mt-0.5" />
                <span>{c.us}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── COVERAGE MAP ────────────────────────────────────────────────── */}
      <section className="bg-gradient-to-br from-slate-900 to-slate-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-8">
            <p className="text-[10px] font-bold tracking-[0.32em] uppercase text-teal-300 mb-3">Coverage</p>
            <h2 className="text-3xl md:text-4xl font-black">Gulf-region dispatch, one center.</h2>
            <p className="mt-4 text-white/70">
              Every route below is served weekly for scheduled concentrate + additive delivery, with
              on-call emergency response.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {COVERAGE.map((c) => (
              <div key={c.state} className="rounded-2xl bg-white/5 border border-white/10 p-5">
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="w-4 h-4 text-teal-300" />
                  <p className="text-lg font-black text-white">{c.state}</p>
                </div>
                <p className="text-sm text-white/70 leading-relaxed">{c.locales}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────────────── */}
      <section className="max-w-4xl mx-auto px-6 py-20">
        <p className="text-[10px] font-bold tracking-[0.32em] uppercase text-teal-700 mb-3">Frequently Asked</p>
        <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-8">
          What buyers ask before they switch.
        </h2>
        <div className="space-y-4">
          {FAQS.map((f, i) => (
            <details
              key={i}
              className="group rounded-xl border border-slate-200 bg-white p-5 open:shadow-md transition-shadow"
            >
              <summary className="cursor-pointer flex items-center justify-between font-bold text-slate-900 gap-4">
                {f.q}
                <ArrowRight className="w-4 h-4 text-slate-400 group-open:rotate-90 transition-transform shrink-0" />
              </summary>
              <p className="mt-3 text-slate-700 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* ── CLOSING CTA ────────────────────────────────────────────────── */}
      <section className="bg-gradient-to-br from-teal-600 via-cyan-700 to-blue-900 text-white">
        <div className="max-w-6xl mx-auto px-6 py-16 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-black leading-tight">
              Talk to a real dialysis specialist.
            </h2>
            <p className="mt-3 text-white/85 text-lg">
              One phone call · one email · one dispatch center. No 800-number call tree,
              no offshore support.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 md:justify-end">
            <Button asChild size="lg" className="bg-white text-slate-900 hover:bg-white/90 font-bold" data-testid="dialysis-cta-call">
              <a href="tel:2814563458">
                <Phone className="w-5 h-5 mr-2" /> 281.456.3458
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white/40 text-white hover:bg-white/10" data-testid="dialysis-cta-email">
              <a href="mailto:info@sterilemate.com?subject=Dialysis facility inquiry">
                <Mail className="w-5 h-5 mr-2" /> info@sterilemate.com
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white/40 text-white hover:bg-white/10" data-testid="dialysis-cta-download">
              <a href="https://sterilemate.com/wp-content/uploads/2022/06/AcidConcentrate-Compressed.pdf" target="_blank" rel="noreferrer">
                <FileText className="w-5 h-5 mr-2" /> Concentrate brochure
              </a>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
